import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, renderSlot as _renderSlot, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "d-flex flex-column align-center justify-center ga-2" }

import { ref, watch, toRef, computed, type Ref, useTemplateRef, onMounted } from 'vue';
import type { PropType } from 'vue';
import type { Map } from 'maplibre-gl';
import MaplibreMap from './MaplibreMap.vue';
import { useEsriLayer } from '@/esri/maplibre/useEsriImageLayer';
import { useFieldOfRegard } from '@/composables/maplibre/useFieldOfRegard';
import { type MoleculeType } from '@/esri/utils';
import type { InitMapOptions, LatLngPair } from '@/types';
import type { MapEventType, Listener } from 'maplibre-gl';
import type { AvailableColorMaps } from "@/colormaps";

import { useTempoStore } from "@/stores/app";


export default /*@__PURE__*/_defineComponent({
  __name: 'EsriMap',
  props: /*@__PURE__*/_mergeModels({
  mapID: { type: String, required: true },
  home: {
    type: Object as PropType<InitMapOptions>,
    required: false,
    default: () => {
      return {
        loc: [39.8283, -98.5795] as LatLngPair, 
        zoom: 4, 
        t: null
      };
    }
  },
  initial: {
    type: Object as PropType<InitMapOptions | null>,
    required: false,
    default: null
  },
  showRoads: {
    type: Boolean,
    required: false,
    default: true
  },
  // events: { moveend: handler, zoomend: handler, click: handler, etc. }
  events: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    type: Object as PropType<Record<keyof MapEventType | string, Listener>>,
    required: false,
    default: () => ({})
  },
  // width and height, default to 100%
  width: {
    type: String,
    required: false,
    default: '100%'
  },
  height: {
    type: String,
    required: false,
    default: '100%'
  },
  // ESRI-specific props
  timestamp: { 
    type: Number as PropType<number | null>, 
    required: false, 
    default: null 
  },
  molecule: { 
    type: String as PropType<MoleculeType>, 
    required: true 
  },
  opacity: { 
    type: Number, 
    required: false, 
    default: 0.9 
  },
  showFieldOfRegard: { 
    type: Boolean, 
    required: false, 
    default: true 
  },
  maplibreLayerName: {
    type: String || undefined,
    required: false,
    default: undefined
  }
}, {
    "latitude": { default: 0 },
    "latitudeModifiers": {},
    "longitude": { default: 0 },
    "longitudeModifiers": {},
    "zoom": { default: 0 },
    "zoomModifiers": {},
  }),
  emits: /*@__PURE__*/_mergeModels(["ready", "zoomhome", "colormap", "esri-timesteps-loaded", "esri-layer"], ["update:latitude", "update:longitude", "update:zoom"]),
  setup(__props, { expose: __expose, emit: __emit }) {

const store = useTempoStore();

const props = __props;

const emit = __emit;

// Reference to inner MaplibreMap exposed API
const innerMap = useTemplateRef<InstanceType<typeof MaplibreMap> | null>("innerMap");
const map = ref<Map | null>(null);

// Pass-through models for view state
const latitude = _useModel<number>(__props, 'latitude');
const longitude = _useModel<number>(__props, 'longitude');
const zoom = _useModel<number>(__props, 'zoom');

// add a watcher to console log the lat/long/zoom changes
watch([latitude, longitude, zoom], ([newLat, newLng, newZoom]) => {
  console.log(`Map view changed: lat=${newLat}, lng=${newLng}, zoom=${newZoom}`);
});

// Internal readiness tracking: single concept (base map ready)
const mapReady = ref(false);              // Underlying Maplibre map style loaded
let promiseResolve: (value: Map) => void;
const onReady: Promise<Map> = new Promise((resolve) => { promiseResolve = resolve; });

const molecule = computed(() => props.molecule);

//https://vuejs.org/api/reactivity-utilities#toref (two-way)
// can also do toRef(()=>props.timestamp) in v 3.3+
const timestampRef = toRef(props, 'timestamp');
const opacityRef = toRef(props, 'opacity');

// ESRI layer composable
const theEsriLayer = useEsriLayer(
  molecule,
  timestampRef,
  opacityRef,
  true,
  props.maplibreLayerName
);

const { loadingEsriTimeSteps, addEsriSource, esriTimesteps, renderOptions } = theEsriLayer;
console.log(theEsriLayer);

watch(esriTimesteps, (newSteps, old) => {
  if (newSteps.length > 0 && newSteps !== old) {
    emit('esri-timesteps-loaded', newSteps);
  }
});

watch(() => renderOptions.value.colormap, cmap => emit("colormap", cmap));

watch(map, (newMap, oldMap) => {
  if (oldMap) {
    store.deregisterMap(oldMap);
  }
  if (newMap) {
    store.registerMap(newMap);
  }
});

const singleDateSelected = computed(() => {
  // a date object with the current day
  if (props.timestamp) {
    const dt = new Date(props.timestamp);
    return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
  }
  return null;
});
// Field of Regard handling
const { 
  addFieldOfRegard, 
  showFieldOfRegard: internalShowFieldOfRegard, 
  updateFieldOfRegard 
} = useFieldOfRegard(singleDateSelected, map as Ref<Map | null>);
  
internalShowFieldOfRegard.value = props.showFieldOfRegard;
watch(() => props.showFieldOfRegard, (val) => {
  internalShowFieldOfRegard.value = val;
});

function onInnerMapReady(m: Map) {
  // Resolve base map readiness immediately
  mapReady.value = true;
  promiseResolve(m);
  console.log('onReady promise on ref exposed API resolved');
  map.value = m;
  // Attach ESRI source right after base readiness (microtask keeps UI snappy)
  addEsriSource(m);
  emit('esri-layer', theEsriLayer);
  console.log('ESRI source added to map');
  emit('ready', m);
  console.log('map ready event emitted');
}

onMounted(() => {
  if (internalShowFieldOfRegard.value) {
    updateFieldOfRegard();
    addFieldOfRegard();
  }
});

// Forward selected parts of inner MaplibreMap API after it's ready
const exposedApi = {
  map,
  mapReady,
  onReady, // Promise resolves when base map ready
  // Forwarded methods
  setView: (center: LatLngPair, zoom: number) => innerMap.value?.setView(center, zoom),
  resetView: () => innerMap.value?.resetView(),
  getCenter: () => innerMap.value?.getCenter(),
  on: (type: keyof MapEventType | string, listener: Listener) => innerMap.value?.on(type, listener),
  off: (type: keyof MapEventType | string, listener: Listener) => innerMap.value?.off(type, listener),
  once: (type: keyof MapEventType | string, listener: Listener) => innerMap.value?.once(type, listener),
  // ESRI specifics
  esriTimesteps,
  loadingEsriTimeSteps,
  renderOptions,
};

__expose(exposedApi);


return (_ctx: any,_cache: any) => {
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_overlay = _resolveComponent("v-overlay")!

  return (_openBlock(), _createBlock(MaplibreMap, {
    mapID: __props.mapID,
    home: __props.home,
    initial: __props.initial,
    "show-roads": __props.showRoads,
    events: __props.events,
    width: __props.width,
    height: __props.height,
    latitude: latitude.value,
    longitude: longitude.value,
    zoom: zoom.value,
    "onUpdate:latitude": _cache[0] || (_cache[0] = (v)=> latitude.value = v),
    "onUpdate:longitude": _cache[1] || (_cache[1] = (v)=> longitude.value = v),
    "onUpdate:zoom": _cache[2] || (_cache[2] = (v)=> zoom.value = v),
    ref_key: "innerMap",
    ref: innerMap,
    onReady: onInnerMapReady,
    onZoomhome: _cache[3] || (_cache[3] = ($event: any) => (_ctx.$emit('zoomhome')))
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_overlay, {
        "model-value": _unref(loadingEsriTimeSteps),
        style: {"z-index":"1000"},
        class: "align-center justify-center",
        contained: "",
        opacity: ".8"
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            _cache[4] || (_cache[4] = _createElementVNode("label", { class: "text-white" }, "Fetching time steps from NASA Earthdata GIS service...", -1)),
            _createVNode(_component_v_progress_circular, {
              size: 80,
              width: 12,
              indeterminate: _unref(loadingEsriTimeSteps),
              color: "#092088"
            }, null, 8, ["indeterminate"])
          ])
        ]),
        _: 1
      }, 8, ["model-value"]),
      _renderSlot(_ctx.$slots, "default")
    ]),
    _: 3
  }, 8, ["mapID", "home", "initial", "show-roads", "events", "width", "height", "latitude", "longitude", "zoom"]))
}
}

})